/**
 * Map truth profitability v2 rows → ProfitPageBundle (Phase 1 ops interim KPIs).
 *
 * Economic honesty: revenue / profit here are TMS-attributed vehicle-day totals,
 * never Xero books. Network charges are not split in this path (Phase 2).
 */

import { profitChartTitle } from './profitReportPeriodRange';
import { cancelledOrderStatus, missingVehicleLabel } from './orderCancellation';
import {
  chartLabel,
  formatProfitGbp,
  type ProfitDailyPoint,
  type ProfitDetailRowMeta,
  type ProfitDetailTable,
  type ProfitKpis,
  type ProfitPageBundle,
  type ProfitReportPeriod,
  type ProfitRevenueImputationNotice,
} from '../data/mockProfitPage';
import type { OrdersDailySeriesDay } from '../types/ordersDailyTruth';
import type {
  RevenueFillBasis,
  TruthAwaitingMergedOrderSummary,
  TruthNetworkCostSummary,
  TruthCustomerDailyRow,
  TruthMergedCostRow,
  TruthMergedOrderRow,
  TruthStatusResponse,
} from '../types/profitabilityTruth';
import type { TruthOtherWorkLine } from '../services/profitabilityTruthClient';
import { displayTonnageClass } from './displayTonnageClass';
import {
  formatPostcodeDistrictLabel,
  isUnknownPostcodeDistrict,
  postcodeDistrict,
} from './postcodeDistrict';
import { extractMergedCostOrderBreakdown } from './profitabilityOrderDisplay';
import type { ProfitMapOrder } from './profitVehicleDayMap';

/** Must match zeecore customerDailyProfitability UNKNOWN_CUSTOMER so order fallbacks join. */
export const UNKNOWN_CUSTOMER = 'Unknown customer';

function customerLabel(name: string | null | undefined): string {
  return String(name ?? '').trim() || UNKNOWN_CUSTOMER;
}

/** Map a truth merged-order row into the shape ProfitVehicleDayMapModal expects. */
export function truthMergedOrderToMapOrder(o: TruthMergedOrderRow): ProfitMapOrder {
  const rev = Number(o.total_revenue_tenant_currency) || 0;
  const cost = o.allocated_cost_gbp != null ? Number(o.allocated_cost_gbp) || 0 : 0;
  const profit = o.profit_gbp != null ? Number(o.profit_gbp) || 0 : rev - cost;
  const date =
    String(o.destination_end_timestamp_local || o.allocation_vehicle_day_date || '').slice(0, 10)
    || String(o.origin_end_timestamp_local || '').slice(0, 10);
  const vehicle =
    String(o.vehicle_observed || o.vehicle_pickup || o.vehicle_assigned || '').trim();
  return {
    orderId: String(o.order_id || '').trim(),
    vehicleId: vehicle,
    date,
    revenue: rev,
    cost,
    profit,
    currency: String(o.tenant_currency || 'GBP'),
    status: String(o.status || ''),
    origin_city: o.origin_city || undefined,
    destination_city: o.destination_city || undefined,
    origin_postal_code: o.origin_postal_code || undefined,
    destination_postal_code: o.destination_postal_code || undefined,
    origin_end_timestamp_local: o.origin_end_timestamp_local || undefined,
    destination_end_timestamp_local: o.destination_end_timestamp_local || undefined,
    vehicle_assigned: o.vehicle_assigned || undefined,
    vehicle_pickup: o.vehicle_pickup || undefined,
    vehicle_observed: o.vehicle_observed || undefined,
    collected_by_vehicle: o.collected_by_vehicle,
    collected_by_depot: o.collected_by_depot,
    delivered_by_vehicle: o.delivered_by_vehicle,
    delivered_by_depot: o.delivered_by_depot,
    trunk_vehicle: o.trunk_vehicle,
    handoff_depot: o.handoff_depot || undefined,
    origin_latitude: o.origin_latitude,
    origin_longitude: o.origin_longitude,
    destination_latitude: o.destination_latitude,
    destination_longitude: o.destination_longitude,
    destination_coords_source: o.destination_coords_source,
    allocation_method: o.allocation_method,
    customer: o.customer || undefined,
    goods_quantity: o.goods_quantity || undefined,
    goods_weight: o.goods_weight || undefined,
    order_import_integration_type: o.order_import_integration_type || undefined,
    revenue_imputed: o.revenue_imputed,
    service_level_name: o.service_level_name || undefined,
  };
}

export type ProfitTruthInputs = {
  costs: TruthMergedCostRow[];
  orders: TruthMergedOrderRow[];
  customers: TruthCustomerDailyRow[];
  /** Jobs carried in the window that no customer row includes yet. */
  customersAwaiting?: TruthAwaitingMergedOrderSummary | null;
  /**
   * Orders tab daily series (by due date) for the admin cross-check of daily
   * delivery / collection counts. null = could not be read; undefined = not fetched.
   */
  ordersSeries?: OrdersDailySeriesDay[] | null;
  /** Read-time network cost coverage from /merged-orders; null = not configured. */
  networkCost?: TruthNetworkCostSummary | null;
  status?: TruthStatusResponse | null;
  period: ProfitReportPeriod;
  range: { startDate: string; endDate: string };
  /** When set (e.g. last 7 days for Yesterday), drives the bar chart only. */
  chartCosts?: TruthMergedCostRow[];
  chartRange?: { startDate: string; endDate: string };
};

/** A separate contract's line on the Profit page: its cost, never a profit, since its revenue is unknown. */
export function otherWorkLineText(line: TruthOtherWorkLine): string {
  const vehicles = line.vehicles.join(', ');
  const days = `${line.vehicle_days} vehicle-day${line.vehicle_days === 1 ? '' : 's'}`;
  const cost = `£${Math.round(line.total_cost_gbp).toLocaleString('en-GB')}`;
  const noGps = line.vehicle_days_without_telematics;
  return `${line.contract} (${vehicles}): ${days}, ${Math.round(line.miles).toLocaleString('en-GB')} miles, ${cost} cost. Revenue not available.`
    + (noGps > 0 ? ` Cost is a minimum: ${noGps} day${noGps === 1 ? ' has' : 's have'} no GPS.` : '');
}

export type ProfitTruthMapResult =
  | { kind: 'bundle'; bundle: ProfitPageBundle }
  | { kind: 'empty'; range: { startDate: string; endDate: string }; status: TruthStatusResponse | null };


function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatPctCell(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  const sign = rounded > 0 ? '' : '';
  return `${sign}${rounded}%`;
}

function formatProfitCell(n: number): string {
  if (n > 0) return `+${formatProfitGbp(n)}`;
  return formatProfitGbp(n);
}

function formatMoneyCell(n: number): string {
  return formatProfitGbp(n);
}

function shortDateLabel(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleString('en-GB', { day: '2-digit', month: 'short' });
}

function parseIsoDate(iso: string): Date | null {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isWeekday(iso: string): boolean {
  const dt = parseIsoDate(iso);
  if (!dt) return false;
  const wd = dt.getDay();
  return wd >= 1 && wd <= 5;
}

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function liftsFromGoods(qty: string | undefined): number {
  const n = Number(String(qty ?? '').replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function liftsFromOrder(o: TruthMergedOrderRow): number {
  return liftsFromGoods(o.goods_quantity);
}

/** Whether the job carries a lift count at all: an empty quantity is unknown, not 0 lifts. */
function liftsKnown(o: TruthMergedOrderRow): boolean {
  return String(o.goods_quantity ?? '').trim() !== '';
}

function liftsByCustomerFromOrders(orders: TruthMergedOrderRow[]): Map<string, number> {
  const byName = new Map<string, number>();
  for (const o of orders) {
    const name = customerLabel(o.customer);
    byName.set(name, (byName.get(name) ?? 0) + liftsFromOrder(o));
  }
  return byName;
}

/** Retained rev from merged orders (incl. Assumed £/job fill) when customer-daily revenue is £0. */
function revenueByCustomerFromOrders(orders: TruthMergedOrderRow[]): Map<string, number> {
  const byName = new Map<string, number>();
  for (const o of orders) {
    const name = customerLabel(o.customer);
    byName.set(name, (byName.get(name) ?? 0) + (Number(o.total_revenue_tenant_currency) || 0));
  }
  return byName;
}

function emptyDetail(heads: string[], footLabel: string): ProfitDetailTable {
  return {
    heads,
    rows: [],
    foot: [footLabel, ...Array(Math.max(0, heads.length - 1)).fill('')],
    moneyCols: [],
  };
}

function eachYmdInclusive(startDate: string, endDate: string): string[] {
  const start = parseIsoDate(startDate);
  const end = parseIsoDate(endDate);
  if (!start || !end || start > end) return [];
  const out: string[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Build daily chart points; for Yesterday/Last week fill every calendar day in range. */
export function buildDailySeries(
  costs: TruthMergedCostRow[],
  period: ProfitReportPeriod,
  seriesRange: { startDate: string; endDate: string },
): ProfitDailyPoint[] {
  const byDate = new Map<string, number>();
  const withMileage = new Set<string>();
  for (const row of costs) {
    const date = String(row.date ?? '').slice(0, 10);
    if (!date) continue;
    // Same vehicle-days as the Net profit card: a day without mileage data has
    // its mileage-based costs missing, so its profit is too high.
    const add = row.telematics_missing === true ? 0 : num(row.profit);
    if (row.telematics_missing !== true) withMileage.add(date);
    byDate.set(date, (byDate.get(date) ?? 0) + add);
  }

  const fillCalendar = period === 'yesterday' || period === 'last-week';
  const dates = fillCalendar
    ? eachYmdInclusive(seriesRange.startDate, seriesRange.endDate)
    : [...byDate.keys()].sort();
  const highlightDate = period === 'yesterday' ? seriesRange.endDate : undefined;

  return dates.map((date, i) => {
    const dt = parseIsoDate(date);
    return {
      day: i + 1,
      label: dt ? chartLabel(dt) : date,
      netProfitGbp: round2(byDate.get(date) ?? 0),
      date,
      ...(highlightDate && date === highlightDate ? { highlighted: true } : {}),
      ...(byDate.has(date) && !withMileage.has(date) ? { noMileageOnly: true as const } : {}),
    };
  });
}

/**
 * The note under the daily chart for days whose vehicle-days all lack mileage
 * data (no bar is drawn, which must not read as £0). A few dates, then a count.
 */
export function noMileageDaysNote(series: ProfitDailyPoint[], shown = 5): string | undefined {
  // DD/MM straight from the ISO date: no clock or zone involved.
  const days = series.filter((p) => p.noMileageOnly && p.date).map((p) => `${p.date!.slice(8, 10)}/${p.date!.slice(5, 7)}`);
  if (days.length === 0) return undefined;
  const more = days.length > shown ? ` and ${days.length - shown} more` : '';
  return `No bar for ${days.length} day${days.length === 1 ? '' : 's'} with no mileage data, so profit is unknown: `
    + `${days.slice(0, shown).join(', ')}${more}.`;
}

/** More bars than this and the x axis labels only Mondays and the 1st (week view keeps all). */
export const DAILY_CHART_DENSE_AFTER = 10;

/**
 * Daily profit chart x-axis text. On a dense axis (month view) every bar keeps
 * its weekday, but only Mondays and the first of the month show the date —
 * "Mon\n(07/09)" — so users can still place each bar. Undefined = use the
 * full label (week view, or a dated bar). Hover and tooltip keep the full label.
 */
export function dailyAxisLabel(point: ProfitDailyPoint, pointCount: number): string | undefined {
  if (pointCount <= DAILY_CHART_DENSE_AFTER || !point.date) return undefined;
  const dt = parseIsoDate(point.date);
  if (!dt || dt.getDay() === 1 || dt.getDate() === 1) return undefined;
  return point.label.split('\n')[0];
}

/** Job-tab column: COL (we collect, network delivers) / DEL (we deliver for another member). */
export const DIRECTION_HEAD = 'Direction';

/** From Palletways col_depot / del_depot vs home depot (zeecore networkCost.ts). */
export function directionLabel(direction: TruthMergedOrderRow['network_direction']): string {
  if (direction === 'outbound') return 'COL';
  if (direction === 'inbound') return 'DEL';
  if (direction === 'local') return 'LOCAL';
  return '—';
}

/** Header of the read-time pallet-network cost column (Job / Customer tabs). */
export const NETWORK_COST_HEAD = 'Network';

/** Cell for one order: £ for outbound with a rate, £0 inbound/local, — unknown. */
function networkCostCell(o: TruthMergedOrderRow): string {
  if (o.network_direction === 'outbound' || o.network_direction === 'unknown' || !o.network_direction) {
    return typeof o.network_cost_gbp === 'number' ? formatMoneyCell(o.network_cost_gbp) : '—';
  }
  return formatMoneyCell(0);
}

/**
 * A month and how its figure was set, in words. Mirrors networkCostPeriodLabel
 * in zeecore/src/projections/profitability/networkCost.ts.
 */
export function networkCostPeriodLabel(month: string, basis: string): string {
  if (basis === 'xero_carried_forward') return `${month} (carried forward)`;
  if (basis === 'xero_monthly_provisional') return `${month} (provisional)`;
  return month;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthWords = (ym: string | undefined) =>
  ym && /^\d{4}-\d{2}$/.test(ym) ? `${SHORT_MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}` : 'its month';

/**
 * Which figure a job's network charge or filled revenue used, in words, for
 * the hover on those cells (plan: xero_monthly_rates_plan.md, step 4).
 * Undefined for any other column, or real (not filled) revenue.
 */
export function jobFigureTitle(head: string, o: TruthMergedOrderRow): string | undefined {
  if (head === NETWORK_COST_HEAD) {
    if (o.network_direction === 'inbound' || o.network_direction === 'local') {
      return `No network charge: ${o.network_direction} job.`;
    }
    const month = monthWords(o.network_cost_period);
    switch (o.network_cost_basis) {
      case 'xero_period_per_outbound': return `Network charge: ${month}, set by hand from Xero.`;
      case 'xero_monthly_final': return `Network charge: ${month}'s own figure from Xero.`;
      case 'xero_monthly_provisional': return `Network charge: ${month}'s provisional figure from Xero; may change while late invoices arrive.`;
      case 'xero_carried_forward': return `Network charge: no figure for this month yet, so ${month}'s is carried forward.`;
      case 'pct_of_revenue': return 'Network charge: a percentage of revenue, as no monthly figure is available.';
      default: return o.network_direction === 'outbound'
        ? 'Network charge unknown: no figure for this month.'
        : 'Network charge unknown: direction unknown (depots missing or not ours).';
    }
  }
  if (head === 'Revenue' && Number(o.revenue_imputed) === 1) {
    return `Assumed: ${revenueFillWords(o.revenue_fill_basis, o.revenue_fill_period, o.network_direction)}. `
      + 'Palletways has not sent this job\'s revenue.';
  }
  return undefined;
}

/**
 * Which figure filled a job's missing revenue, in words. Mirrors
 * revenueFillFor in zeecore/src/projections/profitability/applyNetworkRevenueImputation.ts.
 */
export function revenueFillWords(
  basis: RevenueFillBasis | undefined,
  period: string | undefined,
  direction?: string,
): string {
  const side = direction === 'inbound' ? 'inbound' : 'outbound';
  const month = monthWords(period);
  switch (basis) {
    case 'inbound_allowance': return 'the inbound delivery allowance set in Configuration';
    case 'xero_monthly_final': return `${month}'s average ${side} revenue per job from Xero`;
    case 'xero_monthly_provisional': return `${month}'s provisional average ${side} revenue per job from Xero`;
    case 'xero_carried_forward': return `no figure for this month yet, so ${month}'s average ${side} revenue per job from Xero`;
    default: return 'the single average revenue per job set in Configuration';
  }
}

/**
 * The months' figures that filled revenue, in words, for the Profit page
 * banner and notes (plan step 3), e.g. "Xero's monthly £/job (Sep 2026:
 * £96.79 outbound, £47.66 inbound); £118.43/job for 12 jobs with no monthly
 * figure".
 */
export function revenueImputationMonthsText(notice: Pick<ProfitRevenueImputationNotice, 'avgGbpPerJob' | 'monthsUsed' | 'filledAtSingleAverage'>): string {
  const months = Object.entries(notice.monthsUsed ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const single = `£${notice.avgGbpPerJob.toFixed(2)}/job`;
  if (months.length === 0) return `an assumed average of ${single}`;
  const money = (n: number | null) => (n == null ? 'unknown' : `£${n.toFixed(2)}`);
  const per = months.map(([m, u]) => {
    const tag = u.basis === 'xero_monthly_provisional' ? ' (provisional)' : u.basis === 'xero_carried_forward' ? ' (carried forward)' : '';
    return `${monthWords(m)}${tag}: ${money(u.outbound_per_job_gbp)} outbound, ${money(u.inbound_per_job_gbp)} inbound`;
  }).join('; ');
  const rest = revenueImputationSingleText(notice);
  return `Xero's monthly £/job (${per})${rest ? `; ${rest}` : ''}`;
}

/**
 * The jobs left on the single average, in words a user can act on, e.g.
 * "£118.43/job for 53 jobs we could not match to a monthly figure (their
 * Palletways depots are missing, or Xero has no figure for their month)";
 * empty when none.
 */
export function revenueImputationSingleText(notice: Pick<ProfitRevenueImputationNotice, 'avgGbpPerJob' | 'filledAtSingleAverage'>): string {
  const n = notice.filledAtSingleAverage ?? 0;
  return n > 0
    ? `£${notice.avgGbpPerJob.toFixed(2)}/job for ${n} job${n === 1 ? '' : 's'} we could not match to a monthly figure `
      + `(${n === 1 ? 'its' : 'their'} Palletways depots are missing, or Xero has no figure for ${n === 1 ? 'its' : 'their'} month)`
    : '';
}

/** What one job in a rolled-up row (vehicle-day, customer) says about its figures. */
export interface FigureJob {
  filled: boolean;
  fillBasis?: RevenueFillBasis;
  fillPeriod?: string;
  /** Network charge unknown: outbound with no figure for its month, or direction unknown (depots missing). */
  networkUnknown?: boolean;
  networkBasis?: string;
  networkPeriod?: string;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A fill's figure for a row of mixed directions: the month and basis, not the side. */
function rolledFillWords(basis: RevenueFillBasis | undefined, period: string | undefined): string {
  const month = monthWords(period);
  switch (basis) {
    case 'inbound_allowance': return 'the inbound delivery allowance';
    case 'xero_monthly_final': return `${month}'s figures from Xero`;
    case 'xero_monthly_provisional': return `${month}'s provisional figures from Xero`;
    case 'xero_carried_forward': return `${month}'s figures, carried forward`;
    default: return 'the single average set in Configuration';
  }
}

function networkFigureWords(basis: string, period: string | undefined): string {
  const month = monthWords(period);
  switch (basis) {
    case 'xero_period_per_outbound': return `${month}, set by hand from Xero`;
    case 'xero_monthly_final': return `${month}'s own figure from Xero`;
    case 'xero_monthly_provisional': return `${month}'s provisional figure from Xero`;
    case 'xero_carried_forward': return `${month}'s figure, carried forward`;
    case 'pct_of_revenue': return 'a percentage of revenue';
    default: return 'an unknown figure';
  }
}

/**
 * Which figures a row's jobs used, in words, for the hover on the Revenue and
 * Network cells of rows that sum several jobs (plan: xero_monthly_rates_plan.md,
 * step 4). Undefined when every job's revenue is real, or no job was charged
 * or left unknown.
 */
export function rolledFigureTitle(head: 'Revenue' | 'Network', jobs: FigureJob[]): string | undefined {
  if (head === 'Revenue') {
    const filled = jobs.filter((j) => j.filled);
    if (filled.length === 0) return undefined;
    const groups = new Map<string, number>();
    for (const j of filled) {
      const words = rolledFillWords(j.fillBasis, j.fillPeriod);
      groups.set(words, (groups.get(words) ?? 0) + 1);
    }
    const parts = [...groups.entries()].map(([words, n]) => `${n} at ${words}`);
    return `Assumed revenue on ${filled.length} of ${plural(jobs.length, 'job')}: ${parts.join(', ')}. `
      + `Palletways has not sent their revenue.`;
  }
  const groups = new Map<string, number>();
  for (const j of jobs) {
    if (!j.networkBasis) continue;
    const words = networkFigureWords(j.networkBasis, j.networkPeriod);
    groups.set(words, (groups.get(words) ?? 0) + 1);
  }
  const unknown = jobs.filter((j) => j.networkUnknown).length;
  if (groups.size === 0 && unknown === 0) return undefined;
  const charged = [...groups.entries()].map(([words, n]) => `${plural(n, 'job')} at ${words}`);
  return [
    charged.length > 0 ? `Network charge: ${charged.join('; ')}.` : '',
    unknown > 0 ? `${plural(unknown, 'job')} with an unknown network charge: no figure for the month, or depots missing.` : '',
  ].filter(Boolean).join(' ');
}

/** A vehicle-day's paid jobs (a leg filled at £0 earns nothing that day and is left out). */
function vehicleDayFigureJobs(row: TruthMergedCostRow): FigureJob[] {
  return (row.revenue_breakdown ?? [])
    .filter((b) => (Number(b.revenue) || 0) !== 0 || typeof b.network_cost_gbp === 'number')
    .map((b) => ({
      filled: b.assumed_avg_per_job === true && (Number(b.revenue) || 0) !== 0,
      fillBasis: b.revenue_fill_basis,
      fillPeriod: b.revenue_fill_period,
      networkBasis: b.network_cost_basis,
      networkPeriod: b.network_cost_period,
    }));
}

function orderFigureJob(o: TruthMergedOrderRow): FigureJob {
  return {
    filled: Number(o.revenue_imputed) === 1,
    fillBasis: o.revenue_fill_basis,
    fillPeriod: o.revenue_fill_period,
    // Same rule as the Customer network column (withCustomerNetworkColumn) and the vehicle-day unknown legs.
    networkUnknown: (o.network_direction === 'outbound' || o.network_direction === 'unknown') && typeof o.network_cost_gbp !== 'number',
    networkBasis: typeof o.network_cost_gbp === 'number' ? o.network_cost_basis : undefined,
    networkPeriod: o.network_cost_period,
  };
}

function figureTitles(jobs: FigureJob[], extraUnknown = 0): Partial<Record<string, string>> | undefined {
  const withUnknown = extraUnknown > 0
    ? [...jobs, ...Array.from({ length: extraUnknown }, (): FigureJob => ({ filled: false, networkUnknown: true }))]
    : jobs;
  const revenue = rolledFigureTitle('Revenue', jobs);
  const network = rolledFigureTitle('Network', withUnknown);
  if (!revenue && !network) return undefined;
  return { ...(revenue ? { Revenue: revenue } : {}), ...(network ? { [NETWORK_COST_HEAD]: network } : {}) };
}

/**
 * Plain-language basis for the network column, shown to everyone: the figure is
 * an estimate, and Profit does not subtract it yet (revenue by direction first —
 * docs/architecture/network_cost_profitability.md §8).
 */
export function networkCostNote(summary: TruthNetworkCostSummary): string {
  const periods = Object.entries(summary.periods_used)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, basis]) => networkCostPeriodLabel(month, basis));
  const parts = [
    'Network is pallet-network cost on outbound consignments, estimated per consignment from '
      + `Xero Palletways charges${periods.length ? ` (${periods.join(', ')})` : ''}`
      + ` — range ${formatMoneyCell(summary.total_network_cost_low_gbp)}–${formatMoneyCell(summary.total_network_cost_high_gbp)}.`,
    summary.headline_ready
      ? 'Profit and margin are after network cost. Inbound jobs still carry the full assumed £/job until the delivery allowance is set, so profit may be overstated.'
      : 'Profit and margin do not subtract it (network cost deduction is switched off).',
  ];
  if (summary.headline_ready) {
    parts.push(
      'The Network card and Vehicle tab charge each outbound leg at its vehicle-day month; '
        + 'this tab charges each order once at its booking month, so the totals can differ slightly '
        + '(an order on two vehicle-days is charged twice there, as its revenue is counted twice).',
    );
  }
  if (summary.outbound_unknown_cost > 0) {
    parts.push(`${summary.outbound_unknown_cost} outbound job${summary.outbound_unknown_cost === 1 ? ' has' : 's have'} no rate for its month (—).`);
  }
  const noDepots = summary.unknown_no_depots ?? summary.by_direction.unknown;
  if (noDepots > 0) {
    parts.push(`${noDepots} job${noDepots === 1 ? ' has' : 's have'} no depots, so direction is unknown.`);
  }
  const notHome = summary.unknown_not_home_depot ?? 0;
  if (notHome > 0) {
    parts.push(`${notHome} job${notHome === 1 ? ' has' : 's have'} depots but neither is ours (direction unknown).`);
  }
  return parts.join(' ');
}

/**
 * Admin health check: for each complete month in view, how much of the month's
 * Xero Palletways charges the orders in view account for. A low share means
 * orders not yet computed, or outbound orders without depots.
 */
export function networkReconciliationNote(summary: TruthNetworkCostSummary): string | undefined {
  const months = summary.xero_reconciliation ?? [];
  if (months.length === 0) return undefined;
  // The view is by delivery date but rates are by booking month, so even a
  // fully computed month shows a small gap at the month edges.
  return 'Network cost vs Xero (bookings in view): ' + months
    .map((m) =>
      `${m.month}: ${m.outbound_orders_in_view.toLocaleString('en-GB')} of `
      + `${m.outbound_consignments_declared.toLocaleString('en-GB')} outbound consignments booked that month are in view; `
      + `${formatMoneyCell(m.network_cost_in_view_gbp)} of ${formatMoneyCell(m.palletways_charges_gbp)} `
      + `Xero Palletways charges (${m.coverage_pct}%)`)
    .join('. ')
    + '. This view is by delivery date, so jobs booked near the month end and delivered in the next month are not counted.';
}

function joinNotes(...notes: Array<string | undefined>): string | undefined {
  const parts = notes.filter((n): n is string => !!n);
  return parts.length ? parts.join(' ') : undefined;
}

/**
 * A job's Profit on the Job and Postcode tabs. Once network cost is deducted
 * (headline_ready), outbound jobs show profit after network cost; inbound
 * and local jobs carry none. An outbound job whose network cost is unknown
 * has no headline profit (null), never its own-fleet profit.
 */
function orderHeadlineProfit(
  o: TruthMergedOrderRow,
  summary: TruthNetworkCostSummary | null | undefined,
): number | null {
  const own = o.profit_gbp != null ? num(o.profit_gbp) : num(o.total_revenue_tenant_currency) - num(o.allocated_cost_gbp);
  if (!summary?.headline_ready) return own;
  if (typeof o.profit_after_network_gbp === 'number') return o.profit_after_network_gbp;
  const notOutbound = o.network_direction === 'inbound' || o.network_direction === 'local';
  return notOutbound ? own : null;
}

/** Insert a money column at `idx` (before Profit), shifting column metadata after it. */
function insertColumn(
  table: ProfitDetailTable,
  idx: number,
  head: string,
  cells: string[],
  foot: string,
): ProfitDetailTable {
  const at = idx < 0 ? table.heads.length : idx;
  const put = <T,>(arr: T[], v: T) => [...arr.slice(0, at), v, ...arr.slice(at)];
  const shift = (i: number) => (i >= at ? i + 1 : i);
  return {
    ...table,
    heads: put(table.heads, head),
    rows: table.rows.map((r, i) => put(r, cells[i] ?? '—')),
    foot: put(table.foot, foot),
    moneyCols: [...table.moneyCols.map(shift), at].sort((a, b) => a - b),
    ...(table.typeCol != null ? { typeCol: shift(table.typeCol) } : {}),
    ...(table.mapPinCol != null ? { mapPinCol: shift(table.mapPinCol) } : {}),
  };
}

/** Add the network column before Profit on the Job table (rows map to orders via rowMeta). */
function withJobNetworkColumn(
  table: ProfitDetailTable,
  summary: TruthNetworkCostSummary | null | undefined,
): ProfitDetailTable {
  if (!summary || table.rows.length === 0) return table;
  // By name, so a column added before them cannot shift the switch.
  const PROFIT = table.heads.indexOf('Profit');
  const MARGIN = table.heads.indexOf('Margin');
  let tAfter = 0;
  let tRevAfter = 0;
  const cells: string[] = [];
  const rows = table.rows.map((row, i) => {
    const meta = table.rowMeta?.[i];
    const order = meta?.kind === 'job' ? (meta.order as TruthMergedOrderRow) : undefined;
    cells.push(order ? networkCostCell(order) : '—');
    const next = [...row];
    if (summary.headline_ready && order && PROFIT >= 0 && MARGIN >= 0 && row[PROFIT] !== '—') {
      const value = orderHeadlineProfit(order, summary);
      const rev = num(order.total_revenue_tenant_currency);
      if (value == null) {
        next[PROFIT] = '—';
        next[MARGIN] = '—';
      } else {
        next[PROFIT] = formatProfitCell(value);
        next[MARGIN] = formatPctCell(rev > 0 ? (value / rev) * 100 : 0);
        tAfter += value;
        tRevAfter += rev;
      }
    }
    return next;
  });
  const foot = [...table.foot];
  if (summary.headline_ready && PROFIT >= 0 && MARGIN >= 0) {
    foot[PROFIT] = formatProfitCell(tAfter);
    foot[MARGIN] = formatPctCell(tRevAfter > 0 ? (tAfter / tRevAfter) * 100 : 0);
  }
  return {
    ...insertColumn({ ...table, rows, foot }, PROFIT, NETWORK_COST_HEAD, cells, formatMoneyCell(summary.total_network_cost_gbp)),
    // Basis and reconciliation are pipeline caveats — admin-only.
    adminNote: joinNotes(table.adminNote, networkCostNote(summary), networkReconciliationNote(summary)),
  };
}

/** Append the network column to the Customer table, summed once per order. */
function withCustomerNetworkColumn(
  table: ProfitDetailTable,
  orders: TruthMergedOrderRow[],
  summary: TruthNetworkCostSummary | null | undefined,
): ProfitDetailTable {
  if (!summary || table.rows.length === 0) return table;
  const byName = new Map<string, { gbp: number; unknown: number }>();
  for (const o of orders) {
    const name = customerLabel(o.customer);
    const cur = byName.get(name) ?? { gbp: 0, unknown: 0 };
    if (typeof o.network_cost_gbp === 'number') cur.gbp += o.network_cost_gbp;
    else if (o.network_direction === 'outbound' || o.network_direction === 'unknown') cur.unknown += 1;
    byName.set(name, cur);
  }
  let total = 0;
  const cells = table.rows.map((row) => {
    const agg = byName.get(row[0] ?? '');
    total += agg?.gbp ?? 0;
    // Every job unknown → —; otherwise the known part (partial unknowns are in the note).
    return !agg ? '—' : agg.gbp === 0 && agg.unknown > 0 ? '—' : formatMoneyCell(agg.gbp);
  });
  return {
    ...insertColumn(table, table.heads.indexOf('Profit'), NETWORK_COST_HEAD, cells, formatMoneyCell(total)),
    adminNote: joinNotes(table.adminNote, networkCostNote(summary)),
  };
}

/** Amber line under the Job table: jobs deleted / closed in Palletways, left out of totals. */
export function cancelledJobsNote(count: number): string | undefined {
  if (count <= 0) return undefined;
  return `Totals exclude ${count} job${count === 1 ? '' : 's'} deleted or closed in Palletways without being carried.`;
}

/** Line under a table whose profit/margin totals leave out no-GPS rows. */
function noGpsExclusionNote(count: number, unit: 'vehicle-day' | 'job'): string | undefined {
  if (count <= 0) return undefined;
  return `Profit and margin totals exclude ${count} ${unit}${count === 1 ? '' : 's'} without recorded Samsara mileage data.`;
}

function uncostedJobsNote(count: number): string | undefined {
  if (count <= 0) return undefined;
  return `${count} job${count === 1 ? ' has' : 's have'} no own cost yet: the vehicle's day is not costed, `
    + 'or our leg fell on a day not computed yet. Own cost, profit and margin show as — '
    + 'and are left out of the profit and margin totals.';
}

/** Miles cell for a vehicle-day with no telematics (unknown, not 0). */
export const NO_GPS_CELL = 'No GPS';

/**
 * Jobs cell for a vehicle-day the vehicle drove with no jobs (empty running,
 * repositioning). Its cost counts in fleet totals, not in any job's profit.
 */
export const NO_JOBS_CELL = 'No jobs';

/** Miles cell for a vehicle-day that moved, but less than half a mile (rounds to 0). */
export const NEAR_ZERO_MILES_CELL = '~0 Mile';
/** Miles cell for a vehicle-day whose telematics recorded no movement at all. */
export const ZERO_MILES_CELL = '0 Mile';

function vehicleMilesCell(miles: number, noGps: boolean): string {
  if (noGps) return NO_GPS_CELL;
  if (miles === 0) return ZERO_MILES_CELL;
  if (miles > 0 && miles < 0.5) return NEAR_ZERO_MILES_CELL;
  return String(Math.round(miles));
}

export const VEHICLE_NETWORK_HEAD = 'Network';

/** £ when known; — when the day has legs whose network cost is unknown and nothing priced. */
function vehicleNetworkCell(row: TruthMergedCostRow): string {
  if (typeof row.network_cost_gbp !== 'number') return '—';
  if (row.network_cost_gbp === 0 && num(row.network_cost_unknown_legs) > 0) return '—';
  return formatMoneyCell(row.network_cost_gbp);
}

/**
 * Vehicle table. The Network column only shows when zeecore deducted network
 * cost from these vehicle-days; elsewhere it would be a column of "—".
 */
function buildVehicleDetail(costs: TruthMergedCostRow[]): ProfitDetailTable {
  const table = buildVehicleDetailWithNetwork(costs);
  if (costs.some((r) => typeof r.network_cost_gbp === 'number')) return table;
  return dropColumn(table, table.heads.indexOf(VEHICLE_NETWORK_HEAD));
}

function buildVehicleDetailWithNetwork(costs: TruthMergedCostRow[]): ProfitDetailTable {
  const heads = [
    'Date',
    'Vehicle',
    'Type',
    'Miles',
    'Pickup',
    'Delivery',
    'Jobs',
    'Revenue',
    'Fuel',
    'Driver',
    'Stand.',
    VEHICLE_NETWORK_HEAD,
    'Profit',
    'Margin',
  ];
  if (costs.length === 0) {
    return {
      ...emptyDetail(heads, 'No vehicle-days'),
      moneyCols: [7, 8, 9, 10, 11, 12, 13],
      typeCol: 2,
      footLabelColSpan: 2,
    };
  }

  // Prefer most recent days first; UI pages the full row set.
  const sorted = [...costs].sort((a, b) => {
    const dc = String(b.date).localeCompare(String(a.date));
    if (dc !== 0) return dc;
    return num(a.profit) - num(b.profit);
  });

  let tMiles = 0;
  let tPu = 0;
  let tDel = 0;
  let tJobs = 0;
  let tRev = 0;
  let tFuel = 0;
  let tDriver = 0;
  let tStand = 0;
  let tNetwork = 0;
  let tProfit = 0;
  // Profit/margin totals cover only vehicle-days with telematics; days without
  // it have unknown mileage costs and would read as ~100% margin.
  let tRevKnown = 0;
  let noGpsDays = 0;
  let noGpsRevenue = 0;
  let noJobsDays = 0;

  const rowMeta: ProfitDetailRowMeta[] = [];
  const rows = sorted.map((row) => {
    const noGps = row.telematics_missing === true;
    const bd = extractMergedCostOrderBreakdown(row);
    const pu = num(row.pickup_count) || bd.pickupCount;
    const del = num(row.delivery_count) || bd.deliveryCount;
    // order_count = jobs on the vehicle-day (not pallet lifts — those are Job/Customer tabs).
    const jobs = num(row.order_count) || pu + del;
    if (jobs === 0) noJobsDays += 1;
    const miles = num(row.daily_miles);
    const rev = num(row.revenue);
    const fuel = num(row.jigsaw_fuel_cost);
    const driver = num(row.driver_cost);
    const stand = num(row.depreciation);
    const profit = num(row.profit);
    const margin = num(row.profit_margin);
    tMiles += miles;
    tPu += pu;
    tDel += del;
    tJobs += jobs;
    // Money totals cover the same vehicle-days as Profit (with mileage data),
    // so Revenue − Fuel − Driver − Stand. − Network adds up to the Profit total.
    if (noGps) {
      noGpsDays += 1;
      noGpsRevenue += rev;
    } else {
      tRev += rev;
      tFuel += fuel;
      tDriver += driver;
      tStand += stand;
      tNetwork += num(row.network_cost_gbp);
      tProfit += profit;
      tRevKnown += rev;
    }
    const typeRaw = String(row.vehicle_type || '—').trim() || '—';
    const dateIso = String(row.date ?? '').slice(0, 10);
    rowMeta.push({
      kind: 'vehicle',
      vehicle: String(row.vehicle || '').trim(),
      date: dateIso,
      orderIds: bd.orderIds,
      pickupOrderIds: bd.pickupOrderIds,
      deliveryOrderIds: bd.deliveryOrderIds,
      figureTitles: figureTitles(vehicleDayFigureJobs(row), num(row.network_cost_unknown_legs)),
    });
    return [
      shortDateLabel(String(row.date)),
      String(row.vehicle || '—'),
      typeRaw === '—' ? '—' : displayTonnageClass(typeRaw),
      vehicleMilesCell(miles, noGps),
      String(pu),
      String(del),
      jobs === 0 ? NO_JOBS_CELL : String(jobs),
      formatMoneyCell(rev),
      formatMoneyCell(fuel),
      formatMoneyCell(driver),
      formatMoneyCell(stand),
      vehicleNetworkCell(row),
      noGps ? '—' : formatProfitCell(profit),
      noGps ? '—' : formatPctCell(margin),
    ];
  });

  const tMargin = tRevKnown > 0 ? (tProfit / tRevKnown) * 100 : 0;
  return {
    heads,
    rows,
    rowMeta,
    mapPinCol: 1,
    // Visible to everyone, under the table: every money total leaves out
    // no-GPS days (their rows still show), so the totals add up.
    note: noGpsDays > 0
      ? `Money totals exclude ${noGpsDays} vehicle-day${noGpsDays === 1 ? '' : 's'} without recorded Samsara mileage data `
        + `(${formatMoneyCell(noGpsRevenue)} revenue).`
      : undefined,
    foot: [
      `${costs.length} vehicle-day${costs.length === 1 ? '' : 's'}`,
      '',
      '',
      String(Math.round(tMiles)),
      String(tPu),
      String(tDel),
      String(tJobs),
      formatMoneyCell(tRev),
      formatMoneyCell(tFuel),
      formatMoneyCell(tDriver),
      formatMoneyCell(tStand),
      formatMoneyCell(tNetwork),
      formatProfitCell(tProfit),
      formatPctCell(tMargin),
    ],
    moneyCols: [7, 8, 9, 10, 11, 12, 13],
    typeCol: 2,
    footLabelColSpan: 2,
    // Admin-only: rows already say "No jobs"; the explanation is for admins.
    adminNote: joinNotes(
      noGpsDays > 0
        ? `${noGpsDays} vehicle-day${noGpsDays === 1 ? '' : 's'} had no recorded Samsara mileage data, so mileage and `
          + 'mileage-based costs are unknown. Their profit and margin show as — and are left out of the profit and margin totals.'
        : undefined,
      noJobsDays > 0
        ? `${noJobsDays} vehicle-day${noJobsDays === 1 ? '' : 's'} had no jobs (empty running); their cost is in the totals.`
        : undefined,
    ),
  };
}

/**
 * Job table. The Direction column only exists for orgs with a Palletways home
 * depot configured (zeecore attaches network_direction then); elsewhere it
 * would be a column of "—", so drop it.
 */
function buildJobDetail(orders: TruthMergedOrderRow[]): ProfitDetailTable {
  const table = buildJobDetailWithDirection(orders);
  if (orders.some((o) => o.network_direction)) return table;
  return dropColumn(table, table.heads.indexOf(DIRECTION_HEAD));
}

/** Remove one column and shift the column-index metadata after it. */
function dropColumn(table: ProfitDetailTable, idx: number): ProfitDetailTable {
  if (idx < 0) return table;
  const without = <T,>(arr: T[]) => arr.filter((_, i) => i !== idx);
  const shift = (i: number) => (i > idx ? i - 1 : i);
  return {
    ...table,
    heads: without(table.heads),
    rows: table.rows.map((r) => without(r)),
    foot: without(table.foot),
    moneyCols: table.moneyCols.filter((i) => i !== idx).map(shift),
    typeCol: table.typeCol == null || table.typeCol === idx ? undefined : shift(table.typeCol),
    ...(table.mapPinCol != null ? { mapPinCol: shift(table.mapPinCol) } : {}),
  };
}

export const COLLECTED_BY_HEAD = 'Collected by';
export const DELIVERED_BY_HEAD = 'Delivered by';

/**
 * Job table "Collected by" / "Delivered by": our vehicle, or another depot
 * ("Depot 18" — 102 is ours, so another number is a network member). From
 * zeecore's orderLegVehicles fields; a job not delivered yet shows its
 * Palletways status, a cancelled one its DELETED / JOB CLOSED pill. Older API
 * responses without the fields keep the previous single-vehicle behaviour.
 */
export function jobLegCells(
  o: TruthMergedOrderRow,
  cancelled: string | null,
): { collectedBy: string; deliveredBy: string } {
  if (cancelled) return { collectedBy: '—', deliveredBy: cancelled };
  const depot = (d?: string) => (d && d.trim() ? `Depot ${d.trim()}` : '');
  if (o.collected_by_vehicle === undefined && o.delivered_by_vehicle === undefined) {
    const pickup = String(o.vehicle_pickup || '').trim();
    const observed = String(o.vehicle_observed || o.vehicle_assigned || '').trim();
    return { collectedBy: pickup || '—', deliveredBy: observed || missingVehicleLabel(o) || '—' };
  }
  return {
    collectedBy: o.collected_by_vehicle || depot(o.collected_by_depot) || '—',
    deliveredBy: o.delivered_by_vehicle || depot(o.delivered_by_depot) || missingVehicleLabel(o) || '—',
  };
}

function buildJobDetailWithDirection(orders: TruthMergedOrderRow[]): ProfitDetailTable {
  const heads = [
    'Tracking ID',
    'Customer',
    DIRECTION_HEAD,
    COLLECTED_BY_HEAD,
    DELIVERED_BY_HEAD,
    'Lifts',
    'Revenue',
    'Own cost',
    'Profit',
    'Margin',
  ];
  if (orders.length === 0) {
    return { ...emptyDetail(heads, 'No jobs'), moneyCols: [6, 7, 8, 9], typeCol: 2 };
  }

  const sorted = [...orders].sort((a, b) => num(a.profit_gbp) - num(b.profit_gbp));

  let tLifts = 0;
  let tRev = 0;
  let tCost = 0;
  let tProfit = 0;
  let tRevKnown = 0;
  let noGpsJobs = 0;
  let uncostedJobs = 0;
  let cancelledJobs = 0;
  // Jobs with no lift count from Palletways: "Unknown", and named in the total.
  let unknownLifts = 0;

  const rowMeta: ProfitDetailRowMeta[] = [];
  const rows = sorted.map((o) => {
    // Costed on a no-GPS vehicle-day: cost understated, profit unknown.
    const noGps = o.telematics_missing === true;
    // Deleted / closed in Palletways without being carried: labelled, out of totals.
    const cancelled = cancelledOrderStatus(o);
    const lifts = liftsFromOrder(o);
    const knownLifts = liftsKnown(o);
    if (!knownLifts) unknownLifts += 1;
    const rev = num(o.total_revenue_tenant_currency);
    // No allocation row: the carrying vehicle has no costed vehicle-day (not
    // on the roster), or our leg was on a day not computed yet (an outbound
    // job collected days before its delivery date). Cost is unknown, not £0.
    const uncosted = o.allocated_cost_gbp == null;
    const cost = num(o.allocated_cost_gbp);
    const profit = o.profit_gbp != null ? num(o.profit_gbp) : rev - cost;
    const margin = rev > 0 ? (profit / rev) * 100 : 0;
    if (cancelled) {
      cancelledJobs += 1;
    } else {
      tLifts += lifts;
      tRev += rev;
      tCost += cost;
    }
    if (cancelled) {
      // counted above; no profit either way
    } else if (noGps) {
      noGpsJobs += 1;
    } else if (uncosted) {
      uncostedJobs += 1;
    } else {
      tProfit += profit;
      tRevKnown += rev;
    }
    const { collectedBy, deliveredBy } = jobLegCells(o, cancelled);
    const date =
      String(o.destination_end_timestamp_local || o.allocation_vehicle_day_date || '').slice(0, 10)
      || String(o.origin_end_timestamp_local || '').slice(0, 10);
    rowMeta.push({
      kind: 'job',
      orderId: String(o.order_id || '').trim(),
      vehicleObserved: String(o.vehicle_observed || '').trim(),
      vehiclePickup: String(o.vehicle_pickup || '').trim(),
      date,
      order: o,
    });
    return [
      String(o.order_id || '—'),
      String(o.customer || '—'),
      directionLabel(o.network_direction),
      collectedBy,
      deliveredBy,
      knownLifts ? String(lifts) : 'Unknown',
      formatMoneyCell(rev),
      uncosted && !cancelled ? '—' : formatMoneyCell(cost),
      noGps || cancelled || uncosted ? '—' : formatProfitCell(profit),
      noGps || cancelled || uncosted ? '—' : formatPctCell(margin),
    ];
  });

  const tMargin = tRevKnown > 0 ? (tProfit / tRevKnown) * 100 : 0;
  return {
    heads,
    rows,
    rowMeta,
    mapPinCol: 0,
    note: joinNotes(
      noGpsExclusionNote(noGpsJobs, 'job'),
      uncostedJobsNote(uncostedJobs),
      cancelledJobsNote(cancelledJobs),
    ),
    foot: [
      `${orders.length} job${orders.length === 1 ? '' : 's'}`,
      '',
      '',
      '',
      '',
      unknownLifts > 0 ? `${tLifts} + ${unknownLifts} unknown` : String(tLifts),
      formatMoneyCell(tRev),
      formatMoneyCell(tCost),
      formatProfitCell(tProfit),
      formatPctCell(tMargin),
    ],
    moneyCols: [6, 7, 8, 9],
    // Direction renders as a small label, like Type on the Vehicle tab.
    typeCol: 2,
  };
}

/**
 * Customer rows sit on the delivery day; a job carried in this window but
 * delivered on a day not yet computed (or whose order id never joins) is in
 * none of them. Say so rather than let the Customer total silently trail Jobs.
 */
export function customerAwaitingNote(
  awaiting: TruthAwaitingMergedOrderSummary | null | undefined,
): string | undefined {
  if (!awaiting) return undefined;
  const plural = (n: number) => `${n} job${n === 1 ? '' : 's'}`;
  const notYet = awaiting.jobs > 0
    ? `${plural(awaiting.jobs)} carried in this period (${formatMoneyCell(awaiting.allocated_cost_gbp)} own cost) `
      + 'are not in this table yet: their delivery day has not been computed, '
      + 'or their order could not be matched.'
    : undefined;
  const far = awaiting.outside_delivery_range;
  const range = far && far.days_before === far.days_after
    ? `${far.days_before} days`
    : `${far?.days_before} days before or ${far?.days_after} days after`;
  const outside = far && far.jobs > 0
    ? `${plural(far.jobs)} (${formatMoneyCell(far.allocated_cost_gbp)} own cost) were carried more than `
      + `${range} from their recorded delivery date, so no customer includes that cost.`
    : undefined;
  return joinNotes(notYet, outside);
}

function buildCustomerDetail(
  customers: TruthCustomerDailyRow[],
  orders: TruthMergedOrderRow[],
  awaiting?: TruthAwaitingMergedOrderSummary | null,
  networkCost?: TruthNetworkCostSummary | null,
): ProfitDetailTable {
  const networkByName = new Map<string, number>();
  for (const o of orders) {
    if (typeof o.network_cost_gbp !== 'number') continue;
    const name = customerLabel(o.customer);
    networkByName.set(name, (networkByName.get(name) ?? 0) + o.network_cost_gbp);
  }
  const heads = ['Customer', 'Jobs', 'Lifts', 'Revenue', 'Own cost', 'Profit', 'Margin'];
  const adminNote = customerAwaitingNote(awaiting);
  if (customers.length === 0) {
    return { ...emptyDetail(heads, 'No customers'), moneyCols: [3, 4, 5, 6], adminNote };
  }

  const liftsFromOrders = liftsByCustomerFromOrders(orders);
  const revFromOrders = revenueByCustomerFromOrders(orders);
  const figureJobsByName = new Map<string, FigureJob[]>();
  for (const o of orders) {
    const name = customerLabel(o.customer);
    figureJobsByName.set(name, [...(figureJobsByName.get(name) ?? []), orderFigureJob(o)]);
  }

  // Roll up daily rows by customer name across the window.
  // Prefer stored total_lifts only when every day has the field; otherwise sum orders
  // so a partial recompute cannot undercount.
  const byName = new Map<
    string,
    {
      jobs: number;
      liftsStored: number;
      missingStoredLifts: boolean;
      rev: number;
      cost: number;
      profit: number;
      noGpsJobs: number;
      /** Some day's revenue was filled at the single average. */
      assumed: boolean;
    }
  >();
  for (const c of customers) {
    const name = customerLabel(c.customer_name);
    const cur = byName.get(name) ?? {
      jobs: 0,
      liftsStored: 0,
      missingStoredLifts: false,
      rev: 0,
      cost: 0,
      profit: 0,
      noGpsJobs: 0,
      assumed: false,
    };
    if (c.revenue_assumed_avg_per_job) cur.assumed = true;
    cur.jobs += num(c.order_count);
    cur.noGpsJobs += num(c.orders_without_telematics);
    if (c.total_lifts != null) {
      cur.liftsStored += num(c.total_lifts);
    } else {
      cur.missingStoredLifts = true;
    }
    cur.rev += num(c.total_revenue_gbp);
    cur.cost += num(c.total_allocated_cost_gbp);
    cur.profit += num(c.profit_gbp);
    byName.set(name, cur);
  }

  const rolled = [...byName.entries()]
    .map(([name, v]) => {
      // Customer-daily often has £0 when Palletways has no rate cards; use order revenue
      // (including Assumed £/job fill) so Retained rev. matches Job tab.
      const orderRev = revFromOrders.get(name) ?? 0;
      // Customer-day fills use the single average (no direction); the orders are
      // filled with each month's figure by direction, as on the Job table.
      const useOrders = v.rev === 0 || (v.assumed && orderRev > 0);
      let rev = useOrders ? orderRev : v.rev;
      let profit = useOrders ? round2(rev - v.cost) : v.profit;
      if (networkCost?.headline_ready) {
        // Orders carry revenue by direction (inbound = delivery allowance);
        // customer-day rows are aggregates that cannot, so prefer the orders.
        if (orderRev > 0) rev = orderRev;
        profit = round2(rev - v.cost - (networkByName.get(name) ?? 0));
      }
      return {
        name,
        jobs: v.jobs,
        lifts: v.missingStoredLifts ? (liftsFromOrders.get(name) ?? 0) : v.liftsStored,
        rev,
        cost: v.cost,
        profit,
        noGpsJobs: v.noGpsJobs,
        // Every job costed without GPS: profit is unknown, not near-100%.
        allNoGps: v.jobs > 0 && v.noGpsJobs >= v.jobs,
      };
    })
    .sort((a, b) => a.profit - b.profit);

  let tJobs = 0;
  let tLifts = 0;
  let tRev = 0;
  let tCost = 0;
  let tProfit = 0;
  let tRevKnown = 0;
  let tNoGps = 0;

  const rows = rolled.map((c) => {
    tJobs += c.jobs;
    tLifts += c.lifts;
    tRev += c.rev;
    tCost += c.cost;
    tNoGps += c.noGpsJobs;
    if (!c.allNoGps) {
      tProfit += c.profit;
      tRevKnown += c.rev;
    }
    const margin = c.rev > 0 ? (c.profit / c.rev) * 100 : 0;
    return [
      c.name,
      String(c.jobs),
      String(c.lifts),
      formatMoneyCell(c.rev),
      formatMoneyCell(c.cost),
      c.allNoGps ? '—' : formatProfitCell(c.profit),
      c.allNoGps ? '—' : formatPctCell(margin),
    ];
  });

  const tMargin = tRevKnown > 0 ? (tProfit / tRevKnown) * 100 : 0;
  return {
    heads,
    rows,
    // Customer rows are daily aggregates, so partly no-GPS customers cannot be
    // corrected per job — say how many jobs were costed without GPS.
    note: tNoGps > 0
      ? `${tNoGps} job${tNoGps === 1 ? ' was' : 's were'} costed without recorded Samsara mileage data; profit and margin totals exclude `
        + 'customers whose jobs all lack it.'
      : undefined,
    foot: [
      `${rolled.length} customer${rolled.length === 1 ? '' : 's'}`,
      String(tJobs),
      String(tLifts),
      formatMoneyCell(tRev),
      formatMoneyCell(tCost),
      formatProfitCell(tProfit),
      formatPctCell(tMargin),
    ],
    moneyCols: [3, 4, 5, 6],
    rowMeta: rolled.map((c): ProfitDetailRowMeta => ({
      kind: 'customer',
      figureTitles: figureTitles(figureJobsByName.get(c.name) ?? []),
    })),
    adminNote,
  };
}

function reportPeriodLabel(range: { startDate: string; endDate: string }): string {
  const from = range.startDate.slice(0, 10);
  const to = range.endDate.slice(0, 10);
  if (!from || !to) return '—';
  if (from === to) return from;
  if (from.slice(0, 7) === to.slice(0, 7)) return from.slice(0, 7);
  return `${from} → ${to}`;
}

/**
 * Fold merged-orders by delivery postcode district (Phase 1C).
 * Grain = destination outward code; blank / unparseable → Unknown (never dropped).
 */
function buildPostcodeDetail(
  orders: TruthMergedOrderRow[],
  range: { startDate: string; endDate: string },
  networkCost?: TruthNetworkCostSummary | null,
): ProfitDetailTable {
  const { networkCells, networkTotal, ...table } = buildPostcodeDetailOwn(orders, range, networkCost);
  if (!networkCost || table.rows.length === 0) return table;
  return insertColumn(
    table,
    table.heads.indexOf('Profit'),
    NETWORK_COST_HEAD,
    networkCells ?? [],
    formatMoneyCell(networkTotal ?? 0),
  );
}

function buildPostcodeDetailOwn(
  orders: TruthMergedOrderRow[],
  range: { startDate: string; endDate: string },
  networkCost?: TruthNetworkCostSummary | null,
): ProfitDetailTable & { networkCells?: string[]; networkTotal?: number } {
  const heads = [
    'Period',
    'Postcode district',
    'Pickups',
    'Deliveries',
    'Lifts',
    'Miles',
    'Revenue',
    'Own cost',
    'Profit',
    'Margin',
  ];
  if (orders.length === 0) {
    return {
      ...emptyDetail(heads, 'No postcode districts'),
      moneyCols: [6, 7, 8, 9],
      // Span Period + Postcode district (same pattern as vehicle / job footers).
      footLabelColSpan: 2,
    };
  }

  type Agg = {
    district: string;
    cityVotes: Map<string, number>;
    pickups: number;
    deliveries: number;
    lifts: number;
    miles: number;
    rev: number;
    cost: number;
    profit: number;
    /** Revenue of jobs with GPS-backed cost — the margin denominator. */
    revKnown: number;
    knownJobs: number;
    noGpsJobs: number;
    network: number;
    /** Jobs that may carry network cost we cannot price (outbound no rate, or unknown direction). */
    networkUnknown: number;
    /** Outbound jobs whose network cost is unknown: no headline profit, so left out of it. */
    networkUnknownProfit: number;
    /** Jobs with no cost allocation (vehicle not costed): profit unknown, left out. */
    uncostedJobs: number;
  };

  const byDistrict = new Map<string, Agg>();
  for (const o of orders) {
    const district = postcodeDistrict(o.destination_postal_code);
    const cur = byDistrict.get(district) ?? {
      district,
      cityVotes: new Map<string, number>(),
      pickups: 0,
      deliveries: 0,
      lifts: 0,
      miles: 0,
      rev: 0,
      cost: 0,
      profit: 0,
      revKnown: 0,
      knownJobs: 0,
      noGpsJobs: 0,
      network: 0,
      networkUnknown: 0,
      networkUnknownProfit: 0,
      uncostedJobs: 0,
    };
    cur.deliveries += 1;
    // Multi-leg jobs that had a separate pickup vehicle before delivery to this district.
    if (String(o.vehicle_pickup || '').trim()) cur.pickups += 1;
    cur.lifts += liftsFromOrder(o);
    cur.miles += num(o.allocated_miles);
    const rev = num(o.total_revenue_tenant_currency);
    const cost = num(o.allocated_cost_gbp);
    const profit = orderHeadlineProfit(o, networkCost);
    cur.rev += rev;
    cur.cost += cost;
    if (typeof o.network_cost_gbp === 'number') cur.network += o.network_cost_gbp;
    else if (o.network_direction === 'outbound' || o.network_direction === 'unknown') cur.networkUnknown += 1;
    // Costed on a no-GPS vehicle-day: cost understated, so keep its profit out.
    if (o.telematics_missing === true) {
      cur.noGpsJobs += 1;
    } else if (o.allocated_cost_gbp == null) {
      // Same rule as the Job table: no allocation is unknown cost, not £0.
      cur.uncostedJobs += 1;
    } else if (profit == null) {
      cur.networkUnknownProfit += 1;
    } else {
      cur.profit += profit;
      cur.revKnown += rev;
      cur.knownJobs += 1;
    }
    const city = String(o.destination_city || '').trim();
    if (city && city.toLowerCase() !== 'uk') {
      cur.cityVotes.set(city, (cur.cityVotes.get(city) ?? 0) + 1);
    }
    byDistrict.set(district, cur);
  }

  const period = reportPeriodLabel(range);
  const rolled = [...byDistrict.values()]
    .map((a) => {
      let city: string | undefined;
      let best = 0;
      for (const [name, votes] of a.cityVotes) {
        if (votes > best) {
          best = votes;
          city = name;
        }
      }
      return {
        district: a.district,
        label: formatPostcodeDistrictLabel(a.district, city),
        pickups: a.pickups,
        deliveries: a.deliveries,
        lifts: a.lifts,
        miles: a.miles,
        rev: a.rev,
        cost: a.cost,
        profit: a.profit,
        revKnown: a.revKnown,
        knownJobs: a.knownJobs,
        noGpsJobs: a.noGpsJobs,
        network: a.network,
        networkUnknown: a.networkUnknown,
        networkUnknownProfit: a.networkUnknownProfit,
        uncostedJobs: a.uncostedJobs,
        unknown: isUnknownPostcodeDistrict(a.district),
      };
    })
    // Unknown last among equal profit; otherwise worst profit first.
    .sort((a, b) => {
      if (a.unknown !== b.unknown) return a.unknown ? 1 : -1;
      return a.profit - b.profit;
    });

  let tPu = 0;
  let tDel = 0;
  let tLifts = 0;
  let tMiles = 0;
  let tRev = 0;
  let tCost = 0;
  let tProfit = 0;
  let tRevKnown = 0;
  let tNoGps = 0;
  let tNetwork = 0;
  let tNetworkUnknownProfit = 0;
  let tUncosted = 0;
  const networkCells: string[] = [];

  const rows = rolled.map((r) => {
    tNetwork += r.network;
    tNetworkUnknownProfit += r.networkUnknownProfit;
    // Every job's network cost unknown → —; otherwise the known part.
    networkCells.push(r.network === 0 && r.networkUnknown > 0 ? '—' : formatMoneyCell(r.network));
    tPu += r.pickups;
    tDel += r.deliveries;
    tLifts += r.lifts;
    tMiles += r.miles;
    tRev += r.rev;
    tCost += r.cost;
    tProfit += r.profit;
    tRevKnown += r.revKnown;
    tNoGps += r.noGpsJobs;
    tUncosted += r.uncostedJobs;
    const allNoGps =
      r.knownJobs === 0 && (r.noGpsJobs > 0 || r.networkUnknownProfit > 0 || r.uncostedJobs > 0);
    const margin = r.revKnown > 0 ? (r.profit / r.revKnown) * 100 : 0;
    return [
      period,
      r.label,
      String(r.pickups),
      String(r.deliveries),
      String(r.lifts),
      String(Math.round(r.miles)),
      formatMoneyCell(r.rev),
      formatMoneyCell(r.cost),
      allNoGps ? '—' : formatProfitCell(r.profit),
      allNoGps ? '—' : formatPctCell(margin),
    ];
  });

  const tMargin = tRevKnown > 0 ? (tProfit / tRevKnown) * 100 : 0;
  const districtCount = rolled.length;
  return {
    heads,
    rows,
    note: joinNotes(
      noGpsExclusionNote(tNoGps, 'job'),
      uncostedJobsNote(tUncosted),
      tNetworkUnknownProfit > 0
        ? `Profit and margin totals exclude ${tNetworkUnknownProfit} outbound job${tNetworkUnknownProfit === 1 ? '' : 's'} with unknown network cost.`
        : undefined,
    ),
    networkCells,
    networkTotal: round2(tNetwork),
    foot: [
      `${districtCount} district${districtCount === 1 ? '' : 's'}`,
      '',
      String(tPu),
      String(tDel),
      String(tLifts),
      String(Math.round(tMiles)),
      formatMoneyCell(tRev),
      formatMoneyCell(tCost),
      formatProfitCell(tProfit),
      formatPctCell(tMargin),
    ],
    moneyCols: [6, 7, 8, 9],
    // Span Period + Postcode district (same pattern as vehicle / job footers).
    footLabelColSpan: 2,
  };
}

function comingSoonDetail(_dim: 'run'): ProfitDetailTable {
  return {
    heads: ['Period', 'Run', 'Note'],
    rows: [['—', '—', 'Run rollup coming soon']],
    foot: ['', '', ''],
    moneyCols: [],
    runPillCol: 1,
  };
}

function buildKpis(costs: TruthMergedCostRow[], range: { startDate: string; endDate: string }): ProfitKpis {
  let revenue = 0;
  let fuel = 0;
  let drivers = 0;
  let standing = 0;
  let own = 0;
  let profit = 0;
  // Net profit / margin only over vehicle-days with telematics — days without
  // it have unknown mileage costs and would inflate both.
  let revenueKnown = 0;
  let noGpsDays = 0;
  const days = new Set<string>();

  let noGpsRevenue = 0;
  let noGpsProfit = 0;

  // Every card covers the same vehicle-days as Net profit (with mileage data),
  // so Revenue − Network − Costs = Net profit. Days without it have unknown
  // mileage costs; their revenue is reported beside the Revenue card instead.
  for (const row of costs) {
    if (row.telematics_missing === true) {
      noGpsDays += 1;
      noGpsRevenue += num(row.revenue);
      noGpsProfit += num(row.profit);
    } else {
      revenue += num(row.revenue);
      fuel += num(row.jigsaw_fuel_cost);
      drivers += num(row.driver_cost);
      standing += num(row.depreciation);
      // Network cost sits inside total_cost when deducted; it has its own card.
      own += num(row.total_cost) - num(row.network_cost_gbp);
      profit += num(row.profit);
      revenueKnown += num(row.revenue);
    }
    const date = String(row.date ?? '').slice(0, 10);
    if (date && isWeekday(date)) days.add(date);
  }

  const workingDays = days.size;
  const marginPct = revenueKnown > 0 ? Math.round((profit / revenueKnown) * 1000) / 10 : 0;
  const workingDaysLabel =
    workingDays > 0
      ? `${workingDays} working day${workingDays === 1 ? '' : 's'}`
      : 'no weekday projections';

  return {
    netProfitGbp: round2(profit),
    marginPct,
    // Phase 1: attributed TMS revenue sits in the retained slot; network not split.
    retainedRevenueGbp: round2(revenue),
    customerRevenueGbp: round2(revenue),
    networkChargesGbp: 0,
    ownCostsGbp: round2(own),
    fuelGbp: round2(fuel),
    driversGbp: round2(drivers),
    standingGbp: round2(standing),
    ...(noGpsDays > 0
      ? {
          revenueWithoutTelematics: {
            gbp: round2(noGpsRevenue),
            vehicleDays: noGpsDays,
            netProfitAllDaysGbp: round2(profit + noGpsProfit),
          },
        }
      : {}),
    workingDays: workingDays > 0 ? workingDays : 0,
    stampLabel: `${range.startDate} → ${range.endDate} · ${workingDaysLabel}${
      noGpsDays > 0
        ? ` · figures exclude ${noGpsDays} vehicle-day${noGpsDays === 1 ? '' : 's'} without recorded Samsara mileage data`
        : ''
    }`,
  };
}

function isProbeDisabledWarning(warning: string): boolean {
  return /source probing disabled/i.test(warning);
}

function buildDataQuality(
  status: TruthStatusResponse | null | undefined,
  costs: TruthMergedCostRow[],
): ProfitPageBundle['dataQuality'] {
  const cov = status?.coverage_summary;
  // Profit intentionally calls status with probeSources=false — ignore that info warning.
  const warnings = (status?.warnings ?? []).filter((w) => !isProbeDisabledWarning(w));
  if (cov && cov.total_vehicle_days > 0) {
    const ready = cov.profitability_ready_vehicle_days;
    const total = cov.total_vehicle_days;
    const gapVehicles = cov.vehicles_with_any_gap;
    if (ready < total || gapVehicles > 0) {
      return {
        title: `${total - ready} vehicle-day${total - ready === 1 ? '' : 's'} not profitability-ready`,
        body:
          gapVehicles > 0
            ? `${gapVehicles} vehicle${gapVehicles === 1 ? '' : 's'} have coverage gaps in this window. Totals include only stored vehicle-days — missing days are absent, not zero.`
            : 'Some vehicle-days in range are not ready. Totals reflect stored projections only.',
        actionLabel: 'Open coverage →',
      };
    }
  }
  if (warnings.length > 0) {
    return {
      title: warnings[0]!,
      body: warnings.slice(1).join(' ') || 'See truth profitability status for detail.',
      actionLabel: 'Review status →',
    };
  }
  if (costs.length === 0) return null;
  return null;
}

/**
 * KPI card value for network charges on the ops path. Null when not
 * configured, or when no outbound order was costed (e.g. depots missing
 * until orders are recomputed): unknown, never £0.
 */
export function networkKpiEstimate(
  summary: TruthNetworkCostSummary | null | undefined,
): ProfitKpis['networkEstimate'] {
  if (!summary) return null;
  const outboundWithoutRate = summary.outbound_unknown_cost ?? 0;
  const costedOutboundOrders = Math.max(0, (summary.by_direction?.outbound ?? 0) - outboundWithoutRate);
  if (costedOutboundOrders === 0) return null;
  return {
    gbp: round2(summary.total_network_cost_gbp),
    lowGbp: round2(summary.total_network_cost_low_gbp),
    highGbp: round2(summary.total_network_cost_high_gbp),
    costedOutboundOrders,
    outboundWithoutRate,
  };
}

/**
 * Whether the Network KPI card can show a £ value. Nothing priced while some
 * legs or orders are unknown → "—", never £0.
 */
export function networkKpiKnown(estimate: ProfitKpis['networkEstimate']): boolean {
  if (!estimate) return false;
  return !(estimate.costedOutboundOrders === 0 && estimate.outboundWithoutRate > 0);
}

/**
 * KPI network figure when zeecore deducted network cost from vehicle-days:
 * the same rows as Net profit and the Vehicle tab, so the cards add up.
 * Null when no row carries network cost (not deducted, or nothing costed).
 */
export function vehicleDayNetworkKpi(costs: TruthMergedCostRow[]): ProfitKpis['networkEstimate'] {
  let gbp = 0;
  let legs = 0;
  let unknownLegs = 0;
  let any = false;
  for (const row of costs) {
    if (typeof row.network_cost_gbp !== 'number') continue;
    any = true;
    // Same vehicle-days as Net profit.
    if (row.telematics_missing === true) continue;
    unknownLegs += num(row.network_cost_unknown_legs);
    gbp += row.network_cost_gbp;
    legs += (row.revenue_breakdown ?? []).filter((b) => typeof b.network_cost_gbp === 'number').length;
  }
  // Deduction on (every row carries a figure): use it even when £0, so the
  // card never falls back to the not-deducted order estimate.
  if (!any) return null;
  return {
    gbp: round2(gbp),
    lowGbp: round2(gbp),
    highGbp: round2(gbp),
    costedOutboundOrders: legs,
    outboundWithoutRate: unknownLegs,
    deducted: true,
  };
}

/**
 * Build a ProfitPageBundle from truth v2 fetches, or signal empty store.
 */
export function profitPageFromTruth(input: ProfitTruthInputs): ProfitTruthMapResult {
  const {
    costs,
    orders,
    customers,
    customersAwaiting,
    networkCost,
    ordersSeries,
    status,
    period,
    range,
    chartCosts,
    chartRange,
  } = input;
  const seriesCosts = chartCosts ?? costs;
  const seriesRange = chartRange ?? range;
  const hasAny = costs.length > 0 || orders.length > 0 || customers.length > 0;
  const hasChartData = seriesCosts.length > 0;
  if (!hasAny && !hasChartData) {
    return { kind: 'empty', range, status: status ?? null };
  }

  const kpis = buildKpis(costs, range);
  kpis.networkEstimate = vehicleDayNetworkKpi(costs) ?? networkKpiEstimate(networkCost);
  const computedAt =
    status?.last_computed?.computed_at
    || status?.last_computed?.computedAt
    || null;
  if (computedAt) {
    const when = new Date(computedAt);
    if (!Number.isNaN(when.getTime())) {
      kpis.stampLabel = `${kpis.stampLabel} · synced ${when.toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
      })}`;
    }
  }

  // Deleted / closed without carrying: shown (labelled) on the Job tab only.
  const carriedOrders = orders.filter((o) => !cancelledOrderStatus(o));
  const bundle: ProfitPageBundle = {
    sourcesLabel: 'Xero · Palletways · Samsara',
    kpiMode: 'ops_attributed',
    kpis,
    dailySeries: buildDailySeries(seriesCosts, period, seriesRange),
    chartTitle: profitChartTitle(period),
    dataQuality: buildDataQuality(status, costs),
    detail: {
      vehicle: withOrdersCrossCheck(buildVehicleDetail(costs), costs, ordersSeries),
      job: withJobNetworkColumn(buildJobDetail(orders), networkCost),
      customer: withCustomerNetworkColumn(
        buildCustomerDetail(customers, carriedOrders, customersAwaiting, networkCost),
        carriedOrders,
        networkCost,
      ),
      postcode: buildPostcodeDetail(carriedOrders, range, networkCost),
      run: comingSoonDetail('run'),
    },
    notes: {
      freshness:
        'Ops attribution refreshes when profitability projections recompute. Figures are only as current as the last successful compute for this window.',
      dataQualityFollowUp:
        'Coverage gaps mean missing vehicle-days, not £0 profit. Xero books are Phase 2 — these KPIs must not be read as retained revenue from the ledger.',
      kpis:
        'Attributed revenue is TMS / vehicle-day revenue from truth allocation. Network charges are an estimate for outbound jobs (monthly Xero rate per outbound consignment). When deducted, they are inside net profit, margin and the Network column on the Vehicle tab; inbound jobs still carry the full assumed £/job until the delivery allowance is set, so profit is overstated by that. Margin is profit ÷ attributed revenue. Own costs are fuel + driver + standing (and other allocated costs in total).',
      detail:
        'Vehicle Type shows Artic as 44T (cost key unchanged). Vehicle Jobs is order_count on the vehicle-day. Job/Customer Lifts use goods_quantity (OrderObserved.lifts when quantity absent). Postcode folds merged-orders by delivery district (Unknown for blank/depot-only). Run is not wired yet.',
    },
  };

  return { kind: 'bundle', bundle };
}

/** A day is flagged when Profit and the Orders tab differ by more than this many jobs and 10%. */
const CROSS_CHECK_MIN_JOBS = 3;

export interface OrdersCrossCheckDay {
  date: string;
  profitDeliveries: number;
  ordersDeliveries: number;
  profitCollections: number;
  ordersCollections: number;
}

/**
 * Per day: delivery and collection legs the profit compute counted on our
 * vehicles vs consignments the Orders tab completed. Different methods (per
 * vehicle-day legs vs per order), same Palletways source — a large gap means
 * jobs mis-attributed to vehicles or days. The Orders series is by due date,
 * so a few late/early completions are expected; only clear gaps are flagged.
 */
export function ordersCrossCheckDays(
  costs: TruthMergedCostRow[],
  series: OrdersDailySeriesDay[],
): OrdersCrossCheckDay[] {
  const profit = new Map<string, { del: number; col: number }>();
  for (const row of costs) {
    const date = String(row.date ?? '').slice(0, 10);
    const cur = profit.get(date) ?? { del: 0, col: 0 };
    cur.del += num(row.delivery_count);
    cur.col += (row.revenue_breakdown ?? []).filter((b) => b.leg_role === 'collection').length;
    profit.set(date, cur);
  }
  return series
    .filter((d) => profit.has(d.date))
    .map((d) => ({
      date: d.date,
      profitDeliveries: profit.get(d.date)!.del,
      ordersDeliveries: num(d.summary.deliveries.completed.consignments),
      profitCollections: profit.get(d.date)!.col,
      ordersCollections: num(d.summary.collections.completed.consignments),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function crossCheckGap(profit: number, orders: number): boolean {
  const gap = Math.abs(profit - orders);
  return gap > CROSS_CHECK_MIN_JOBS && gap > orders * 0.1;
}

/**
 * Saturday (YYYY-MM-DD) starting the Sat–Fri week holding `date`. Saturday
 * starts the week so weekend jobs done the following Monday stay in it.
 */
function weekStart(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const back = (d.getUTCDay() + 1) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

/**
 * Admin note on the Vehicle tab: weeks whose counts disagree with the Orders
 * tab. Compared by week (Sat–Fri), not by day: the Orders series counts a job
 * on its due date and Profit on the day it was done, so a Saturday job
 * delivered on Monday is a day-level gap every week but cancels in the week.
 */
export function ordersCrossCheckNote(
  costs: TruthMergedCostRow[],
  series: OrdersDailySeriesDay[] | null | undefined,
): string | undefined {
  if (series === undefined || costs.length === 0) return undefined;
  if (series === null) return 'Cross-check vs Orders tab: the Orders daily figures could not be read, so it was not run.';
  const days = ordersCrossCheckDays(costs, series);
  if (days.length === 0) return undefined;
  const weeks = new Map<string, OrdersCrossCheckDay>();
  for (const d of days) {
    const key = weekStart(d.date);
    const w = weeks.get(key) ?? { date: key, profitDeliveries: 0, ordersDeliveries: 0, profitCollections: 0, ordersCollections: 0 };
    w.profitDeliveries += d.profitDeliveries;
    w.ordersDeliveries += d.ordersDeliveries;
    w.profitCollections += d.profitCollections;
    w.ordersCollections += d.ordersCollections;
    weeks.set(key, w);
  }
  const all = [...weeks.values()].sort((a, b) => a.date.localeCompare(b.date));
  const flagged = all.filter((w) =>
    crossCheckGap(w.profitDeliveries, w.ordersDeliveries) || crossCheckGap(w.profitCollections, w.ordersCollections));
  const unit = (n: number) => `week${n === 1 ? '' : 's'}`;
  if (flagged.length === 0) {
    return `Cross-check vs Orders tab: deliveries and collections agree in all ${all.length} ${unit(all.length)}.`;
  }
  const shown = flagged.slice(0, 5).map((w) =>
    `week of ${w.date}: deliveries ${w.profitDeliveries} vs ${w.ordersDeliveries}, collections ${w.profitCollections} vs ${w.ordersCollections}`);
  return `Cross-check vs Orders tab (Profit vs Orders, by Sat–Fri week): ${flagged.length} of ${all.length} ${unit(all.length)} differ — `
    + shown.join('; ')
    + (flagged.length > shown.length ? `; and ${flagged.length - shown.length} more` : '')
    + '. A part week at the edge of the view can differ because jobs are due and done on different days.';
}

function withOrdersCrossCheck(
  table: ProfitDetailTable,
  costs: TruthMergedCostRow[],
  series: OrdersDailySeriesDay[] | null | undefined,
): ProfitDetailTable {
  const note = ordersCrossCheckNote(costs, series);
  return note ? { ...table, adminNote: joinNotes(table.adminNote, note) } : table;
}
